import { Dispatch, useEffect, useState } from 'react';
import Web3 from 'web3';

import { setChainId as setLocalChainId } from '@/store/actions/app.action';
import { useAppDispatch } from '@/store/utils';

declare global {
  interface Window {
    ethereum?: any;
  }
}

interface ChainListenerResult {
  chainId: number | null;
  networkName: string | null;
  account: string | null;
  error: string | null;
  isConnected: boolean;
}

// Network name mapping
const CHAIN_NAMES: { [key: number]: string } = {
  1: 'Ethereum Mainnet',
  5: 'Goerli',
  11155111: 'Sepolia',
  137: 'Polygon Mainnet',
  80001: 'Mumbai',
  56: 'BSC Mainnet',
  97: 'BSC Testnet',
  42161: 'Arbitrum One',
  421613: 'Arbitrum Goerli',
};

export const useChainListener = (): ChainListenerResult => {
  const dispatch = useAppDispatch();
  const [chainId, setChainId] = useState<number | null>(null);
  const [networkName, setNetworkName] = useState<string | null>(null);
  const [account, setAccount] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState<boolean>(false);

  const updateChainInfo = (newChainId: number) => {
    setChainId(newChainId);
    setNetworkName(CHAIN_NAMES[newChainId] || `Unknown Network (${newChainId})`);
    dispatch(setLocalChainId(newChainId));
  };

  useEffect(() => {
    let web3: Web3;

    const initialize = async (): Promise<void> => {
      try {
        if (typeof window.ethereum !== 'undefined') {
          web3 = new Web3(window.ethereum);

          const currentChainIdHex = await window.ethereum.request({ method: 'eth_chainId' });
          const currentChainId = parseInt(currentChainIdHex as string, 16);
          updateChainInfo(currentChainId);

          const accounts = (await window.ethereum.request({ method: 'eth_accounts' })) as string[];
          if (accounts[0]) {
            setAccount(accounts[0]);
            setIsConnected(true);
          }

          window.ethereum.on('chainChanged', (newChainId: string) => {
            const chainIdNumber = parseInt(newChainId, 16);
            updateChainInfo(chainIdNumber);
          });

          window.ethereum.on('accountsChanged', (newAccounts: string[]) => {
            setAccount(newAccounts[0] || null);
            setIsConnected(!!newAccounts[0]);
          });

          window.ethereum.on('connect', ({ chainId: connectedChainId }: { chainId: string }) => {
            const chainIdNumber = parseInt(connectedChainId, 16);
            updateChainInfo(chainIdNumber);
            setIsConnected(true);
          });

          window.ethereum.on('disconnect', (error: { code: number; message: string }) => {
            setIsConnected(false);
            setError(`Disconnected: ${error.message}`);
          });
        } else {
          setError('Please install MetaMask to use this feature');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An unknown error occurred');
      }
    };

    initialize();

    return () => {
      if (window.ethereum) {
        window.ethereum.removeListener('chainChanged', () => {});
        window.ethereum.removeListener('accountsChanged', () => {});
        window.ethereum.removeListener('connect', () => {});
        window.ethereum.removeListener('disconnect', () => {});
      }
    };
  }, []);

  return { chainId, networkName, account, error, isConnected };
};
